import { GroupedList, SelectionMode, assertNever, FocusZoneTabbableElements, Stack, } from '@fluentui/react';
import React, { FC, useContext } from "react";
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SelectedItem, ItemState } from "./types";
import { SelectedItemRow, SelectedItemRowDragHoverCallback, SelectedItemRowDropCallback } from './SelectedItemRow';
import { IconName } from './ItemRow';
import { removeTextKey } from './utils/text';
import { itemAlignmentStackTokensGap } from './utils/style';

export interface SelectedColumnsProps {
  headerName: string;
  selectedItems: SelectedItem[];
  onItemOperationClick: (selectedItem?: SelectedItem) => void;
  onSelectedItemsChange: (newItems: SelectedItem[]) => void;
  onRemoveAllClick: () => void;
}

export const SelectedColumns: FC<SelectedColumnsProps> = (props) => {
  const { selectedItems, headerName, onItemOperationClick, onSelectedItemsChange, onRemoveAllClick, } = props;

  const i18n: any = useContext(I18nContext);
  const removeAllText: string = i18n.getString(_TL_('Remove all'));

  const onDraggingHover: SelectedItemRowDragHoverCallback = (draggingItemData, clientOffset) => {
    console.log(`draggingItem:${draggingItemData.friendlyName}`);
    const containerElement = document.querySelector('.selected-items-list .ms-GroupedList-group');
    const selectedColumnItemElements = containerElement.querySelectorAll('.item-row');
    const dragIndex = draggingItemData.index;
    const draggedElement = selectedColumnItemElements[dragIndex];
    // assume all items have same height
    const itemHeight = draggedElement.getBoundingClientRect().height;
    const containerTop = containerElement.getBoundingClientRect().top;
    const clientYOffset = clientOffset.y;
    let hoverElementIndex = Math.floor((clientYOffset - containerTop) / itemHeight);
    hoverElementIndex = hoverElementIndex < 0 ? 0 : hoverElementIndex;
    hoverElementIndex = hoverElementIndex > selectedColumnItemElements.length - 1 ? selectedColumnItemElements.length - 1 : hoverElementIndex;
    // eslint-disable-next-line no-param-reassign
    draggingItemData.hoverIndex = hoverElementIndex;
    const transformUp1Style = getTransfromStyleString(-1, itemHeight);
    const transformDown1Style = getTransfromStyleString(1, itemHeight);
    const draggedElementTransformStyle = getTransfromStyleString(hoverElementIndex - dragIndex, itemHeight);
    const lowerBound = Math.min(hoverElementIndex, dragIndex);
    const upperBound = Math.max(hoverElementIndex, dragIndex);
    for (let i = 0; i < selectedColumnItemElements.length; i += 1) {
      const currentElement = selectedColumnItemElements[i] as HTMLElement;
      currentElement.style.transition = 'all 0.2s ease-out 0s';
      switch (true) {
        case i < lowerBound:
        case i > upperBound:
          currentElement.style.transform = null;
          break;
        case i === dragIndex:
          currentElement.style.transform = draggedElementTransformStyle;
          break;
        default:
          if (lowerBound === hoverElementIndex) { currentElement.style.transform = transformDown1Style; } else { currentElement.style.transform = transformUp1Style; }
          break;
      }
    }
  }

  const onDrop: SelectedItemRowDropCallback = (draggingItemData) => {
    const containerElement = document.querySelector('.selected-items-list .ms-GroupedList-group');
    const selectedColumnItemElements = containerElement.querySelectorAll('.item-row');
    selectedColumnItemElements.forEach((element) => {
      // eslint-disable-next-line no-param-reassign
      (element as HTMLElement).style.transform = null;
      // eslint-disable-next-line no-param-reassign
      (element as HTMLElement).style.transition = null;
    });

    const newItems = [...selectedItems];
    const [draggingItem] = newItems.splice(draggingItemData.index, 1);
    newItems.splice(draggingItemData.hoverIndex, 0, draggingItem);
    onSelectedItemsChange(newItems);
    // focus on droped element
    setTimeout(() => {
      const dropIndex = draggingItemData.hoverIndex;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const containerElement = document.querySelector('.selected-items-list .ms-GroupedList-group');
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const selectedColumnItemElements = containerElement.querySelectorAll('.item-row');
      (selectedColumnItemElements[dropIndex] as HTMLElement).focus();
    }, 0);
  }

  return (
    <div className="list-wrapper selected-list">
      <div className="list-header"> {headerName}</div>
      <Stack className="action-group" horizontal tokens={itemAlignmentStackTokensGap}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <span className="operation enabled" tabIndex={0} onClick={onRemoveAllClick} data-testId="remove-all-button">
          {removeAllText}
        </span>
      </Stack>
      <GroupedList
        className="selected-items-list item-list"
        selectionMode={SelectionMode.none}
        items={selectedItems}
        onRenderCell={onRenderSelectedCell}
        onShouldVirtualize={() => false}
        focusZoneProps={{
          handleTabKey: FocusZoneTabbableElements.all,
          preventFocusRestoration: true,
        }}
      />
    </div>
  );

  function onRenderSelectedCell(nestingDepth?: number, item?: SelectedItem, itemIndex?: number): React.ReactNode {
    if (!item) {
      throw new Error("unknown item");
    }
    if (itemIndex === undefined) {
      throw new Error("unknown itemIndex, find the reason rather than just use it.");
    }
    return (
      <SelectedItemRow
        {...getStaticPropsOfSelectedItemRowByItem(item, i18n, { removeTextKey })}
        index={itemIndex}
        data={item}
        iconName={item.state === ItemState.mandatory ? IconName.Empty : IconName.GripperBarHorizontal}
        onOperationTrigger={onItemOperationClick}
        draggingHoverCallback={onDraggingHover}
        dropCallback={onDrop}
      />
    );
  }
}

function getTransfromStyleString(offset: number | undefined, height: number): string {
  return offset === undefined ? "" : `translate(0px, ${offset * height}px)`;
}

export function getStaticPropsOfSelectedItemRowByItem(item: SelectedItem, i18n, texts: { removeTextKey: string }) {
  return {
    index: -1,
    text: item.friendlyName ?? '',
    iconName: IconName.Empty,
    isOperationEnabled: getIsOperationEnabledForSelectedItem(item),
    operationText: getOperationTextForSelectedItem(item, i18n.getString(texts.removeTextKey)),
  }
}

function getOperationTextForSelectedItem(item: SelectedItem, removeText) {
  return item.state === ItemState.optional ? removeText : '';
}

// eslint-disable-next-line consistent-return
function getIsOperationEnabledForSelectedItem(item: SelectedItem) {
  switch (item.state) {
    case ItemState.mandatory:
    case ItemState.lock:
    case ItemState.unknown:
      return false;
    case ItemState.optional:
      return true;
    default:
      assertNever(item.state);
  }
}